import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import itemsData from '@/data/items.json';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { usePotionShortcutStore, type PotionKind } from '@/state/potionShortcutStore';
import { inventory, type InventoryItem } from '@/systems/InventorySystem';
import { ensureRealtime } from '@/network/realtime';
import { ui } from './theme';
import { ItemIcon } from './ItemIcon';
import { CloseButton } from './CloseButton';

interface ItemTpl {
  id: number;
  name: string;
  type: string;
  subtype?: string;
  slot?: string;
  icon?: string;
  effect?: string;
  value?: number;
  level?: number;
  def?: number;
  atkMin?: number;
  atkMax?: number;
  matkMin?: number;
  matkMax?: number;
  stack?: number;
}
const TPL: Record<number, ItemTpl> = Object.fromEntries((itemsData.items as ItemTpl[]).map((i) => [i.id, i]));

// Mirror of backend computeSellPrice (inventory/handler.go) for UI preview.
// Backend remains the source of truth for the actual transaction.
function estimateSellPrice(tpl: ItemTpl | null | undefined, upgradeLevel: number): number {
  if (!tpl) return 0;
  const levelReq = tpl.level ?? 1;
  let base = 0;
  switch (tpl.type) {
    case 'weapon': {
      const avg = Math.floor(((tpl.atkMin ?? tpl.matkMin ?? 0) + (tpl.atkMax ?? tpl.matkMax ?? 0)) / 2);
      base = 50 + avg * 8 + levelReq * 15;
      break;
    }
    case 'armor':
    case 'shield':
      base = 40 + (tpl.def ?? 0) * 10 + levelReq * 12;
      break;
    case 'consumable':
      base = 5 + levelReq * 2;
      break;
    case 'material':
    case 'scroll':
      base = tpl.subtype === 'upgrade' ? 1500 : 30 + levelReq * 5;
      break;
    default:
      base = 20 + levelReq * 5;
  }
  if (upgradeLevel > 0) {
    let mult = 1;
    for (let i = 0; i < upgradeLevel; i++) mult *= 1.2;
    base = Math.floor(base * mult);
  }
  return Math.max(1, base);
}



function isPotion(tpl: ItemTpl | null | undefined, kind: PotionKind): boolean {
  if (!tpl) return false;
  return tpl.type === 'consumable' && tpl.effect === kind;
}

type EquipSlotKey =
  | 'helmet' | 'pendant' | 'main_hand' | 'off_hand' | 'chest'
  | 'pants' | 'gloves' | 'boots' | 'belt' | 'ring1' | 'ring2';

interface EquipSlotDef {
  key: EquipSlotKey;
  label: string;
  glyph: string;
  col: number; // 1..3
  row: number; // 1..5
  matchSlot: string[]; // template.slot values that fit here
}

// MU-style 3-col x 5-row paper doll layout
const EQUIP_SLOTS: EquipSlotDef[] = [
  { key: 'helmet',    label: 'Mũ',     glyph: 'H', col: 2, row: 1, matchSlot: ['helmet', 'head'] },
  { key: 'pendant',   label: 'Dây',    glyph: 'N', col: 3, row: 1, matchSlot: ['pendant', 'neck'] },
  { key: 'main_hand', label: 'Vũ Khí', glyph: 'W', col: 1, row: 2, matchSlot: ['main_hand'] },
  { key: 'chest',     label: 'Áo',     glyph: 'A', col: 2, row: 2, matchSlot: ['chest'] },
  { key: 'off_hand',  label: 'Khiên',  glyph: 'D', col: 3, row: 2, matchSlot: ['off_hand'] },
  { key: 'gloves',    label: 'Găng',   glyph: 'G', col: 1, row: 3, matchSlot: ['gloves'] },
  { key: 'belt',      label: 'Đai',    glyph: 'B', col: 2, row: 3, matchSlot: ['belt'] },
  { key: 'pants',     label: 'Quần',   glyph: 'P', col: 2, row: 4, matchSlot: ['pants', 'legs'] },
  { key: 'ring1',     label: 'Nhẫn 1', glyph: 'R', col: 1, row: 4, matchSlot: ['ring', 'ring1'] },
  { key: 'ring2',     label: 'Nhẫn 2', glyph: 'R', col: 3, row: 4, matchSlot: ['ring', 'ring2'] },
  { key: 'boots',     label: 'Giày',   glyph: 'F', col: 2, row: 5, matchSlot: ['boots', 'feet'] },
];

// Backend slot_index mapping (must match backend equipSlotIndex)
const EQUIP_SLOT_INDEX: Record<EquipSlotKey, number> = {
  main_hand: 0, off_hand: 1, helmet: 2, chest: 3, pants: 4,
  gloves: 5, boots: 6, belt: 7, pendant: 8, ring1: 9, ring2: 10,
};

// Compact ghost-style mini button (avoids the big atlas-backed secondaryBtn).
const miniBtnStyle = (danger: boolean): CSSProperties => ({
  width: 'auto',
  height: 24,
  padding: danger ? '0 8px' : '0 10px',
  fontSize: 11,
  lineHeight: '22px',
  background: danger ? 'rgba(231,76,60,0.15)' : 'rgba(255,209,102,0.12)',
  color: danger ? '#ff8a7a' : '#ffd166',
  border: `1px solid ${danger ? '#a8332a' : '#7a5d1a'}`,
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: '"Philosopher", serif',
  fontWeight: 600,
  letterSpacing: 0.3,
  textShadow: '0 1px 1px rgba(0,0,0,0.7)',
});

export function Inventory() {
  const ch = useCharacterStore((s) => s.current);
  const close = () => useUIStore.getState().set('inventory', false);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sellConfirm, setSellConfirm] = useState<{ itemId: number; quantity: number; label: string; total: number; unit: number } | null>(null);
  const [sortedOrder, setSortedOrder] = useState<number[] | null>(null);

  const hpTemplateId = usePotionShortcutStore((s) => s.hpTemplateId);
  const mpTemplateId = usePotionShortcutStore((s) => s.mpTemplateId);
  const setShortcut = usePotionShortcutStore((s) => s.setShortcut);
  const clearShortcut = usePotionShortcutStore((s) => s.clearShortcut);

  useEffect(() => {
    if (!ch) return;
    inventory.setChar(ch.id);
    inventory.refresh().then(setItems).catch(() => setItems([]));
  }, [ch]);

  const selectedItem = useMemo(
    () => (selectedId !== null ? items.find((x) => x.id === selectedId) ?? null : null),
    [items, selectedId],
  );
  const selectedTpl = selectedItem ? TPL[selectedItem.templateId] : null;
  const selectedKind: PotionKind | null = selectedTpl
    ? (isPotion(selectedTpl, 'heal') ? 'heal' : isPotion(selectedTpl, 'mana') ? 'mana' : null)
    : null;

  const slotMap = useMemo(() => {
    const SLOTS = 64;
    const map: (InventoryItem | null)[] = Array.from({ length: SLOTS }, () => null);
    const bag = items.filter((x) => (x.slotType ?? 'inventory') === 'inventory');

    if (sortedOrder) {
      const byId = new Map(bag.map((it) => [it.id, it]));
      let cursor = 0;
      for (const id of sortedOrder) {
        const it = byId.get(id);
        if (it && cursor < SLOTS) {
          map[cursor++] = it;
          byId.delete(id);
        }
      }
      for (const it of byId.values()) {
        if (cursor >= SLOTS) break;
        map[cursor++] = it;
      }
      return map;
    }

    const pending: InventoryItem[] = [];
    for (const it of bag) {
      if (it.slotIndex !== null && it.slotIndex !== undefined && it.slotIndex >= 0 && it.slotIndex < SLOTS && map[it.slotIndex] === null) {
        map[it.slotIndex] = it;
      } else {
        pending.push(it);
      }
    }
    let cursor = 0;
    for (const it of pending) {
      while (cursor < SLOTS && map[cursor] !== null) cursor++;
      if (cursor >= SLOTS) break;
      map[cursor] = it;
      cursor++;
    }
    return map;
  }, [items, sortedOrder]);

  const equippedMap = useMemo(() => {
    const map: Partial<Record<EquipSlotKey, InventoryItem>> = {};
    const equipped = items.filter((x) => x.slotType === 'equipped');
    const byIndex = new Map<number, InventoryItem>();
    for (const it of equipped) {
      if (it.slotIndex !== null && it.slotIndex !== undefined) {
        byIndex.set(it.slotIndex, it);
      }
    }
    for (const def of EQUIP_SLOTS) {
      const idx = EQUIP_SLOT_INDEX[def.key];
      const it = byIndex.get(idx);
      if (it) map[def.key] = it;
    }
    return map;
  }, [items]);

  const hpTotal = useMemo(
    () => (hpTemplateId ? items.filter((x) => x.templateId === hpTemplateId).reduce((s, x) => s + x.quantity, 0) : 0),
    [items, hpTemplateId],
  );
  const mpTotal = useMemo(
    () => (mpTemplateId ? items.filter((x) => x.templateId === mpTemplateId).reduce((s, x) => s + x.quantity, 0) : 0),
    [items, mpTemplateId],
  );

  const assign = (kind: PotionKind) => {
    if (!selectedItem || !selectedTpl) return;
    if (!isPotion(selectedTpl, kind)) {
      useUIStore.getState().showToast(
        kind === 'heal' ? 'Item này không phải bình HP' : 'Item này không phải bình MP',
        'warn',
      );
      return;
    }
    setShortcut(kind, selectedTpl.id);
    useUIStore.getState().showToast(
      `Đã gán ${selectedTpl.name} làm bình auto ${kind === 'heal' ? 'HP' : 'MP'}`,
      'info',
    );
  };

  const isUsable = (tpl: ItemTpl | null | undefined): boolean => {
    if (!tpl) return false;
    return tpl.type === 'consumable' || tpl.type === 'scroll' || tpl.type === 'petbox';
  };

  const isEquippable = (tpl: ItemTpl | null | undefined): boolean => {
    if (!tpl) return false;
    return (tpl.type === 'weapon' || tpl.type === 'armor' || tpl.type === 'shield') && !!tpl.slot;
  };

  const resolveEquipSlot = (tplSlot: string): string => {
    if (tplSlot === 'ring') {
      const ring1 = equippedMap.ring1;
      const ring2 = equippedMap.ring2;
      if (!ring1) return 'ring1';
      if (!ring2) return 'ring2';
      return 'ring1';
    }
    return tplSlot;
  };

  const equipItem = async () => {
    if (!selectedItem || !selectedTpl || !selectedTpl.slot) return;
    try {
      await inventory.equip(selectedItem.id, resolveEquipSlot(selectedTpl.slot));
      useUIStore.getState().showToast(`Đã trang bị ${selectedTpl.name}`, 'info');
      const refreshed = await inventory.refresh();
      setItems(refreshed);
    } catch (e) {
      useUIStore.getState().showToast((e as Error).message ?? 'Không trang bị được', 'error');
    }
  };

  const unequipItem = async (item: InventoryItem) => {
    try {
      await inventory.unequip(item.id);
      const tpl = TPL[item.templateId];
      useUIStore.getState().showToast(`Đã tháo ${tpl?.name ?? `#${item.templateId}`}`, 'info');
      const refreshed = await inventory.refresh();
      setItems(refreshed);
    } catch (e) {
      useUIStore.getState().showToast((e as Error).message ?? 'Không tháo được', 'error');
    }
  };

  const sellItem = () => {
    if (!selectedItem || !selectedTpl) return;
    if (selectedItem.slotType === 'equipped') {
      useUIStore.getState().showToast('Hãy tháo trang bị trước khi bán', 'warn');
      return;
    }
    const qty = selectedItem.quantity > 1 ? selectedItem.quantity : 1;
    const label = selectedTpl.name + (qty > 1 ? ` ×${qty}` : '');
    const unit = estimateSellPrice(selectedTpl, selectedItem.upgradeLevel ?? 0);
    setSellConfirm({ itemId: selectedItem.id, quantity: qty, label, unit, total: unit * qty });
  };

  const confirmSell = async () => {
    if (!sellConfirm) return;
    const { itemId, quantity, label } = sellConfirm;
    setSellConfirm(null);
    try {
      const resp = await inventory.sell(itemId, quantity);
      const cs = useCharacterStore.getState();
      cs.setCurrency({ zen: resp.zen });
      useUIStore.getState().showToast(`Đã bán ${label} (+${resp.totalPrice} Zen)`, 'info');
      const refreshed = await inventory.refresh();
      setItems(refreshed);
      if (!refreshed.find((x) => x.id === itemId)) {
        setSelectedId(null);
      }
    } catch (e) {
      useUIStore.getState().showToast((e as Error).message ?? 'Không bán được', 'error');
    }
  };

  const useItem = async () => {
    if (!selectedItem || !selectedTpl) return;
    if (!isUsable(selectedTpl)) {
      useUIStore.getState().showToast('Item này không thể dùng', 'warn');
      return;
    }
    const kind: PotionKind | undefined = selectedKind ?? undefined;
    try {
      const cli = await ensureRealtime();
      const resp = await cli.request<{
        ok: boolean; reason?: string; itemId?: number; templateId?: number;
        category?: string; kind?: string; value?: number;
        multiplier?: number; durationS?: number;
        petTemplateId?: number; petId?: number;
        hp?: number; mp?: number;
      }>('field.useitem', { itemId: selectedItem.id, kind });
      if (!resp || !resp.ok) {
        useUIStore.getState().showToast(`Không dùng được (${resp?.reason ?? 'lỗi'})`, 'error');
        await inventory.refresh().then(setItems).catch(() => {});
        return;
      }
      if (resp.category === 'potion') {
        const cs = useCharacterStore.getState();
        const patch: { hp?: number; mp?: number } = {};
        if (typeof resp.hp === 'number') patch.hp = resp.hp;
        if (typeof resp.mp === 'number') patch.mp = resp.mp;
        if (patch.hp !== undefined || patch.mp !== undefined) cs.setVitals(patch);
        useUIStore.getState().showToast(
          `Đã dùng ${selectedTpl.name}${resp.value ? ` (+${resp.value})` : ''}`,
          'info',
        );
      } else if (resp.category === 'scroll') {
        const mins = Math.round((resp.durationS ?? 0) / 60);
        useUIStore.getState().showToast(
          `Buff ${resp.kind?.toUpperCase()} ×${resp.multiplier?.toFixed(1)} trong ${mins} phút`,
          'info',
        );
        useUIStore.getState().bumpBuffRefresh();
      } else if (resp.category === 'petbox') {
        useUIStore.getState().showToast(
          `Mở hộp thành công, nhận pet (template #${resp.petTemplateId})`,
          'info',
        );
        useUIStore.getState().set('pet', true);
      } else {
        useUIStore.getState().showToast(`Đã dùng ${selectedTpl.name}`, 'info');
      }
      const refreshed = await inventory.refresh();
      setItems(refreshed);
      if (!refreshed.find((x) => x.id === selectedItem.id)) {
        setSelectedId(null);
      }
    } catch (e) {
      useUIStore.getState().showToast((e as Error).message ?? 'Lỗi mạng', 'error');
    }
  };

  const slotBox = (kind: PotionKind, tplId: number | null, total: number) => {
    const tpl = tplId ? TPL[tplId] : null;
    const label = kind === 'heal' ? 'HP' : 'MP';
    const tone = kind === 'heal' ? '#e74c3c' : '#3498db';
    const toneDim = kind === 'heal' ? 'rgba(231,76,60,0.15)' : 'rgba(52,152,219,0.15)';
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
        border: `1px solid ${tone}`, borderRadius: 6,
        background: `linear-gradient(90deg, ${toneDim} 0%, rgba(11,18,32,0.9) 60%)`,
      }}>
        <div style={{
          fontSize: 11, color: tone, fontWeight: 700,
          width: 26, textAlign: 'center', letterSpacing: 0.5,
        }}>{label}</div>
        <div style={{
          width: 34, height: 34, background: '#000',
          border: `1px solid ${tone}`, borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 18,
        }}
        title={tpl ? `${tpl.name} (×${total})` : 'Chưa gán'}>
          {tpl ? <ItemIcon templateId={tpl.id} size={30} /> : <span style={{ opacity: 0.5 }}>?</span>}
        </div>
        <div style={{ fontSize: 11, color: '#e8eef7', flex: 1, overflow: 'hidden', minWidth: 0 }}>
          {tpl ? <>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{tpl.name}</div>
            <div style={{ opacity: 0.8, fontSize: 10 }}>×{total}</div>
          </> : <span style={{ opacity: 0.55, fontStyle: 'italic' }}>Chọn bình rồi gán</span>}
        </div>
        <button
          style={miniBtnStyle(false)}
          onClick={() => assign(kind)}
          disabled={!selectedItem || selectedKind !== kind}
        >Gán</button>
        {tpl && (
          <button
            style={miniBtnStyle(true)}
            onClick={() => clearShortcut(kind)}
            title="Bỏ gán"
          >×</button>
        )}
      </div>
    );
  };

  const renderEquipSlot = (def: EquipSlotDef) => {
    const it = equippedMap[def.key];
    const tpl = it ? TPL[it.templateId] : null;
    const isSelected = it && selectedId === it.id;
    return (
      <div
        key={def.key}
        title={it ? (tpl ? `${tpl.name}${it.upgradeLevel ? ` +${it.upgradeLevel}` : ''} (click chọn, dbl-click tháo)` : `Item #${it.templateId}`) : def.label}
        onClick={() => it && setSelectedId(selectedId === it.id ? null : it.id)}
        onDoubleClick={() => it && unequipItem(it)}
        style={{
          gridColumn: def.col, gridRow: def.row,
          width: 48, height: 48,
          background: it ? '#0e1830' : 'rgba(10,15,26,0.7)',
          border: isSelected ? '2px solid #ffeb3b' : `1px solid ${it ? '#6b5320' : '#3a4555'}`,
          color: '#fff', textAlign: 'center', overflow: 'hidden',
          cursor: it ? 'pointer' : 'default', boxSizing: 'border-box',
          position: 'relative', borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {it ? (
          <>
            <ItemIcon templateId={it.templateId} size={42} />
            {it.upgradeLevel > 0 && (
              <div style={{
                position: 'absolute', left: 2, top: 0,
                fontSize: 10, color: '#ffeb3b', textShadow: '0 0 2px #000', fontWeight: 700,
              }}>+{it.upgradeLevel}</div>
            )}
          </>
        ) : (
          <div style={{
            fontSize: 10, color: '#5a6478', fontStyle: 'italic',
          }}>{def.label}</div>
        )}
      </div>
    );
  };

  const TYPE_ORDER: Record<string, number> = {
    weapon: 0, shield: 1, armor: 2,
    consumable: 3, scroll: 4, petbox: 5, material: 6,
  };

  const sortBag = () => {
    const bag = items.filter((x) => (x.slotType ?? 'inventory') === 'inventory');
    const sorted = [...bag].sort((a, b) => {
      const ta = TPL[a.templateId];
      const tb = TPL[b.templateId];
      const oa = ta ? (TYPE_ORDER[ta.type] ?? 99) : 100;
      const ob = tb ? (TYPE_ORDER[tb.type] ?? 99) : 100;
      if (oa !== ob) return oa - ob;
      if (a.templateId !== b.templateId) return a.templateId - b.templateId;
      if ((b.upgradeLevel ?? 0) !== (a.upgradeLevel ?? 0)) return (b.upgradeLevel ?? 0) - (a.upgradeLevel ?? 0);
      return (b.quantity ?? 0) - (a.quantity ?? 0);
    });
    setSortedOrder(sorted.map((it) => it.id));
    useUIStore.getState().showToast('Đã sắp xếp túi đồ', 'info');
  };

  return (
    <div style={{ position: 'absolute', top: 70, right: 10, width: 560, zIndex: 100, ...ui.panel }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, paddingBottom: 8,
        borderBottom: '1px solid rgba(255,209,102,0.25)',
      }}>
        <strong style={{
          fontSize: 16, color: '#ffd166', letterSpacing: 0.8,
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
        }}>Túi Đồ &amp; Trang Bị</strong>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            style={miniBtnStyle(false)}
            onClick={sortBag}
            title="Sắp xếp túi đồ theo loại"
          >Sắp xếp</button>
          <CloseButton onClick={close} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* Paper-doll equipment panel (MU-style) */}
        <div style={{
          width: 188, padding: '10px 8px',
          background: 'linear-gradient(180deg, rgba(11,18,32,0.95) 0%, rgba(7,12,22,0.95) 100%)',
          border: '1px solid #4a3a18', borderRadius: 6,
          boxShadow: 'inset 0 0 12px rgba(0,0,0,0.6)',
        }}>
          <div style={{
            fontSize: 12, color: '#ffd166', marginBottom: 8, textAlign: 'center',
            letterSpacing: 1, fontWeight: 700,
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          }}>
            TRANG BỊ
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 48px)',
            gridTemplateRows: 'repeat(5, 48px)',
            gap: 8, justifyContent: 'center',
          }}>
            {EQUIP_SLOTS.map(renderEquipSlot)}
          </div>
        </div>

        {/* Inventory bag side */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {slotBox('heal', hpTemplateId, hpTotal)}
            {slotBox('mana', mpTemplateId, mpTotal)}
          </div>

          <div style={{
            padding: 6,
            background: 'rgba(7,12,22,0.6)',
            border: '1px solid #2a3548', borderRadius: 6,
            display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4,
          }}>
            {Array.from({ length: 64 }).map((_, slot) => {
              const it = slotMap[slot];
              const tpl = it ? TPL[it.templateId] : null;
              const isSelected = it && selectedId === it.id;
              const isHpShortcut = tpl && hpTemplateId === tpl.id;
              const isMpShortcut = tpl && mpTemplateId === tpl.id;
              const border = isSelected
                ? '2px solid #ffeb3b'
                : isHpShortcut
                  ? '2px solid #e74c3c'
                  : isMpShortcut
                    ? '2px solid #3498db'
                    : '1px solid #324a5e';
              const tooltip = it
                ? (tpl
                    ? `${tpl.name}${it.quantity > 1 ? ` ×${it.quantity}` : ''}${it.upgradeLevel ? ` +${it.upgradeLevel}` : ''}`
                    : `Item #${it.templateId}${it.quantity > 1 ? ` ×${it.quantity}` : ''}`)
                : '';
              return (
                <div key={slot} title={tooltip}
                  onClick={() => it && setSelectedId(selectedId === it.id ? null : it.id)}
                  onDoubleClick={() => {
                    if (!it) return;
                    const t = TPL[it.templateId];
                    if (t && (t.type === 'weapon' || t.type === 'armor' || t.type === 'shield') && t.slot) {
                      inventory.equip(it.id, resolveEquipSlot(t.slot))
                        .then(() => inventory.refresh().then(setItems))
                        .catch((e: Error) => useUIStore.getState().showToast(e.message, 'error'));
                    }
                  }}
                  style={{
                    aspectRatio: '1 / 1',
                    background: it ? '#0e1830' : '#0a111f',
                    border,
                    borderRadius: 3,
                    fontSize: 10, color: '#fff', textAlign: 'center', overflow: 'hidden',
                    cursor: it ? 'pointer' : 'default', boxSizing: 'border-box',
                    position: 'relative',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {it ? (
                    <>
                      <ItemIcon templateId={it.templateId} size={32} />
                      {it.quantity > 1 && (
                        <div style={{
                          position: 'absolute', right: 2, bottom: 0,
                          fontSize: 10, color: '#fff', textShadow: '0 0 2px #000', fontWeight: 700,
                        }}>×{it.quantity}</div>
                      )}
                      {it.upgradeLevel > 0 && (
                        <div style={{
                          position: 'absolute', left: 2, top: 0,
                          fontSize: 10, color: '#ffeb3b', textShadow: '0 0 2px #000', fontWeight: 700,
                        }}>+{it.upgradeLevel}</div>
                      )}
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {selectedItem && (
        <div style={{
          marginTop: 10, padding: 10, fontSize: 11, color: '#cfd8dc',
          background: 'linear-gradient(180deg, rgba(11,18,32,0.95) 0%, rgba(7,12,22,0.95) 100%)',
          border: '1px solid #4a3a18', borderRadius: 6,
          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <div style={{
              width: 28, height: 28, background: '#000', border: '1px solid #324a5e',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><ItemIcon templateId={selectedItem.templateId} size={26} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', color: '#fff' }}>
                {selectedTpl ? selectedTpl.name : `Item #${selectedItem.templateId}`}
                {selectedItem.upgradeLevel > 0 && (
                  <span style={{ color: '#ffeb3b' }}> +{selectedItem.upgradeLevel}</span>
                )}
                {selectedItem.slotType === 'equipped' && (
                  <span style={{ color: '#7fdbff', marginLeft: 6, fontSize: 10 }}>[Đang trang bị]</span>
                )}
              </div>
              <div style={{ fontSize: 10, opacity: 0.8 }}>
                {selectedTpl?.type ?? 'unknown'}
                {selectedTpl?.subtype ? ` / ${selectedTpl.subtype}` : ''}
                {selectedItem.quantity > 1 ? ` · ×${selectedItem.quantity}` : ''}
              </div>
            </div>
          </div>
          {selectedTpl?.effect && <div>Hiệu ứng: {selectedTpl.effect}{selectedTpl.value ? ` (+${selectedTpl.value})` : ''}</div>}
          {(() => {
            const lv = Math.max(0, Math.min(15, selectedItem.upgradeLevel ?? 0));
            const mult = 1 + 0.03 * lv;
            const fmt = (v: number) => (Number.isInteger(v) ? v.toString() : v.toFixed(1));
            return (
              <>
                {selectedTpl?.def !== undefined && (
                  <div>
                    DEF: {fmt(selectedTpl.def * mult)}
                    {lv > 0 && <span style={{ color: '#9aa', fontSize: 10 }}> (cơ bản {selectedTpl.def}, +{(lv * 3)}%)</span>}
                  </div>
                )}
                {(selectedTpl?.atkMin !== undefined || selectedTpl?.atkMax !== undefined) && (
                  <div>
                    ATK: {fmt((selectedTpl.atkMin ?? 0) * mult)}-{fmt((selectedTpl.atkMax ?? 0) * mult)}
                    {lv > 0 && <span style={{ color: '#9aa', fontSize: 10 }}> (cơ bản {selectedTpl.atkMin}-{selectedTpl.atkMax}, +{(lv * 3)}%)</span>}
                  </div>
                )}
                {(selectedTpl?.matkMin !== undefined || selectedTpl?.matkMax !== undefined) && (
                  <div>
                    MATK: {fmt((selectedTpl.matkMin ?? 0) * mult)}-{fmt((selectedTpl.matkMax ?? 0) * mult)}
                    {lv > 0 && <span style={{ color: '#9aa', fontSize: 10 }}> (cơ bản {selectedTpl.matkMin}-{selectedTpl.matkMax}, +{(lv * 3)}%)</span>}
                  </div>
                )}
              </>
            );
          })()}
          {selectedTpl?.slot && <div>Slot: {selectedTpl.slot}</div>}
          {selectedTpl?.level !== undefined && selectedTpl.level > 0 && (() => {
            const req = selectedTpl.level;
            const cur = ch?.level ?? 0;
            const ok = cur >= req;
            return (
              <div style={{ color: ok ? '#cfd8dc' : '#ff6b6b' }}>
                Level yêu cầu: {req}
                {!ok && <span style={{ fontSize: 10 }}> (bạn: {cur})</span>}
              </div>
            );
          })()}
          {selectedItem.slotType !== 'equipped' && (() => {
            const unit = estimateSellPrice(selectedTpl, selectedItem.upgradeLevel ?? 0);
            const total = unit * (selectedItem.quantity > 1 ? selectedItem.quantity : 1);
            return (
              <div style={{ color: '#ffd166' }}>
                Giá bán: {total.toLocaleString()} Zen
                {selectedItem.quantity > 1 && (
                  <span style={{ color: '#9aa', fontSize: 10 }}> ({unit.toLocaleString()}/cái)</span>
                )}
              </div>
            );
          })()}
          {selectedKind && (
            <div style={{ color: selectedKind === 'heal' ? '#e74c3c' : '#3498db' }}>
              {selectedKind === 'heal' ? 'Bình HP - có thể gán shortcut' : 'Bình MP - có thể gán shortcut'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {isUsable(selectedTpl) && (
              <button
                style={{ ...ui.primaryBtn, padding: '4px 10px', fontSize: 11 }}
                onClick={useItem}
                title="Dùng item này"
              >Dùng</button>
            )}
            {isEquippable(selectedTpl) && selectedItem.slotType !== 'equipped' && (
              <button
                style={{ ...ui.primaryBtn, padding: '4px 10px', fontSize: 11 }}
                onClick={equipItem}
                title={`Trang bị vào ${selectedTpl?.slot}`}
              >Trang bị</button>
            )}
            {selectedItem.slotType === 'equipped' && (
              <button
                style={{ ...ui.secondaryBtn, padding: '4px 10px', fontSize: 11 }}
                onClick={() => unequipItem(selectedItem)}
              >Tháo</button>
            )}
            {selectedItem.slotType !== 'equipped' && (
              <button
                style={{ ...ui.secondaryBtn, padding: '4px 10px', fontSize: 11, color: '#ffd166', borderColor: '#ffd166' }}
                onClick={sellItem}
                title="Bán item lấy Zen"
              >Bán</button>
            )}
            {selectedKind === 'heal' && (
              <button
                style={{ ...ui.secondaryBtn, padding: '4px 10px', fontSize: 11 }}
                onClick={() => assign('heal')}
              >Gán HP</button>
            )}
            {selectedKind === 'mana' && (
              <button
                style={{ ...ui.secondaryBtn, padding: '4px 10px', fontSize: 11 }}
                onClick={() => assign('mana')}
              >Gán MP</button>
            )}
          </div>
        </div>
      )}

      {sellConfirm && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setSellConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              ...ui.panel, minWidth: 320, padding: 20, textAlign: 'center',
              border: '2px solid #ffd166',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: '#ffd166', marginBottom: 8 }}>
              Xác nhận bán
            </div>
            <div style={{ color: '#e8eef7', marginBottom: 8, fontSize: 13 }}>
              Bạn có chắc muốn bán <strong>{sellConfirm.label}</strong>?
            </div>
            <div style={{ color: '#ffd166', marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
              Nhận: {sellConfirm.total.toLocaleString()} Zen
              {sellConfirm.quantity > 1 && (
                <span style={{ color: '#9aa', fontSize: 11, fontWeight: 400 }}>
                  {' '}({sellConfirm.unit.toLocaleString()} × {sellConfirm.quantity})
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                style={{ ...ui.primaryBtn, padding: '6px 16px' }}
                onClick={confirmSell}
              >Bán</button>
              <button
                style={{ ...ui.secondaryBtn, padding: '6px 16px' }}
                onClick={() => setSellConfirm(null)}
              >Hủy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
